<template>
    <div>
        <div v-if="authUserPermission['kbli-view']">
            <base-header class="pb-6">
                <div class="row align-items-center py-4">
                    <div class="col-lg-6 col-7">
                        <h6 class="h2 text-white d-inline-block mb-0"></h6>
                    </div>
                </div>
            </base-header>
            <div class="container-fluid mt--7">
                <div class="card mb-4">
                    <div class="card-header border-0">
                        <div class="row align-items-center">
                            <div class="col-md-8">
                                <h3 class="mb-0">KBLI</h3>
                            </div>
                            <div class="col-md-4">
                                <el-input v-model="filterText" placeholder="Search KBLI..." prefix-icon="el-icon-search"
                                    clearable size="small" class="border-radius-20" @input="handleFilter">
                                </el-input>
                            </div>
                        </div>
                    </div>

                    <div v-if="!loading" class="card-body" style="max-height: 600px; overflow-y: auto;">
                        <el-tree ref="kbliTree" :data="treeData" :props="treeProps" node-key="id"
                            :default-expand-all="false" :expand-on-click-node="false" @node-expand="handleNodeExpand"
                            class="kbli-tree">
                            <span class="custom-tree-node" slot-scope="{ node, data }">
                                <span class="tree-node-left">
                                    <span class="kbli-code">{{ data.code }}</span>
                                </span>
                                <span class="tree-node-content">
                                    <span class="kbli-description">{{ data.description }}</span>
                                    <span class="kbli-definition" v-if="data.definition">{{ data.definition }}</span>
                                </span>
                            </span>
                        </el-tree>

                        <div class="text-center mt-4" v-if="treeData.length === 0">
                            <img src="/assets/smartcat/data_is_empty.png" alt="No Data" width="250px">
                            <p class="text-muted mt-3">No KBLI data found</p>
                        </div>
                    </div>

                    <div v-else class="card-body">
                        <page-loading />
                    </div>

                    <!-- Pagination -->
                    <div class="card-footer pb-0" v-if="pagination.total > 0 && !filterText">
                        <span class="float-left">
                            <base-pagination :page-count="pagination.last_page" v-model="pagination.current_page"
                                @click.native="changePage(pagination.current_page)" size="sm"></base-pagination>
                        </span>
                        <span class="float-right">
                            {{ (pagination.current_page - 1) * pagination.per_page + treeData.length }} of {{
                            pagination.total }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <noaccess v-else />
    </div>
</template>

<script>
import { mapState } from "vuex";
import Api from '@/helpers/api';
import KbliService from '@/services/dictionary/kbli.service';

export default {
    name: 'KbliList',
    data() {
        return {
            loading: false,
            filterText: '',
            treeData: [],
            treeProps: {
                label: 'code',
                children: 'children'
            },
            pagination: {
                current_page: 1,
                per_page: 10,
                last_page: 1,
                total: 0
            },
            filterTimeout: null
        };
    },
    computed: {
        ...mapState({
            authUserPermission: (state) => state.auth.userPermission,
        }),
    },
    watch: {
        treeData: {
            handler(newData) {
                this.$nextTick(() => {
                    this.attachLazyLoad(newData);
                });
            },
            deep: true
        }
    },
    mounted() {
        this.loadKbliData();
    },
    methods: {
        loadKbliData() {
            this.loading = true;
            const params = {
                search: this.filterText,
                page: this.pagination.current_page,
                per_page: this.pagination.per_page,
            };

            Api(this, KbliService.get(params))
                .onSuccess((response) => {
                    const resData = response.data.data;
                    this.treeData = this.processTreeData(resData.data);
                    this.pagination.total = resData.total;
                    this.pagination.current_page = resData.current_page;
                    this.pagination.last_page = resData.last_page;
                })
                .onError((error) => {
                    this.$notify({
                        type: 'danger',
                        message: 'Failed to fetch KBLI data.',
                        verticalAlign: 'bottom',
                        horizontalAlign: 'left'
                    });
                    this.treeData = [];
                })
                .onFinish(() => {
                    this.loading = false;
                })
                .call();
        },

        processTreeData(data) {
            const processNode = (node) => {
                if (node.children && node.children.length > 0) {
                    // Node sudah punya children dari API, process recursive
                    node.children = node.children.map(child => processNode(child));
                } else if (node.hasChildren) {
                    // Node punya children tapi belum di-load
                    // Buat dummy placeholder agar toggle arrow muncul
                    node.children = [{
                        id: `loading-${node.id}`,
                        code: 'Loading...',
                        description: 'Loading...',
                        definition: '',
                        hasChildren: false,
                        _isPlaceholder: true
                    }];
                    node._needsLazyLoad = true;
                }
                return node;
            };

            return data.map(item => processNode(item));
        },

        attachLazyLoad(nodes) {
            const attachToNode = (node) => {
                if (node._needsLazyLoad && !node._lazyLoadAttached) {
                    node._lazyLoadAttached = true;
                }
                if (node.children) {
                    node.children.forEach(child => attachToNode(child));
                }
            };

            nodes.forEach(node => attachToNode(node));
        },

        handleNodeExpand(data, node) {
            // Cek apakah node ini perlu lazy load
            if (data._needsLazyLoad) {
                // Panggil API untuk load children
                Api(this, KbliService.show(data.code))
                    .onSuccess((response) => {
                        const item = response.data.data;
                        let children = item.children_recursive || [];

                        // Process children secara RECURSIVE untuk semua level
                        const processChildren = (nodes) => {
                            return nodes.map(child => {
                                // Jika child punya children_recursive, gunakan itu sebagai children
                                if (child.children_recursive && child.children_recursive.length > 0) {
                                    child.children = processChildren(child.children_recursive);
                                    delete child.children_recursive; // Hapus yang lama
                                } else if (child.hasChildren) {
                                    // Child punya hasChildren tapi belum ada data, buat placeholder
                                    child.children = [{
                                        id: `loading-${child.id}`,
                                        code: 'Loading...',
                                        description: 'Loading...',
                                        definition: '',
                                        hasChildren: false,
                                        _isPlaceholder: true
                                    }];
                                    child._needsLazyLoad = true;
                                }
                                return child;
                            });
                        };

                        children = processChildren(children);

                        // Update data dengan children yang baru (hapus placeholder)
                        this.$set(data, 'children', children);
                        this.$set(data, '_needsLazyLoad', false);
                    })
                    .onError((error) => {
                        this.$notify({
                            type: 'danger',
                            message: `Failed to load children for ${data.code}`,
                            verticalAlign: 'bottom',
                            horizontalAlign: 'left'
                        });
                    })
                    .call();
            }
        },

        handleFilter() {
            if (this.filterTimeout) clearTimeout(this.filterTimeout);
            this.filterTimeout = setTimeout(() => {
                this.pagination.current_page = 1;
                this.loadKbliData();
            }, 500);
        },

        changePage(page) {
            this.loading = true;
            clearTimeout(this.filterTimeout);
            this.filterTimeout = setTimeout(() => {
                this.loadKbliData();
            }, 100);
        }
    },
};
</script>

<style scoped>
.kbli-tree {
    font-size: 14px;
}

.border-radius-20>>>.el-input__inner {
    border-radius: 20px;
}

.kbli-tree>>>.el-tree-node__content {
    height: auto;
    min-height: 45px;
    padding: 10px 0;
    border-bottom: 1px solid #e9ecef;
}

.kbli-tree>>>.el-tree-node__content:hover {
    background-color: #f8f9fa;
}

.custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    padding-right: 12px;
    gap: 8px;
}

.tree-node-left {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.tree-node-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.kbli-code {
    font-weight: 700;
    color: #5e72e4;
    font-size: 15px;
    padding: 2px 8px;
    background: #f0f3ff;
    border-radius: 4px;
}

.kbli-description {
    color: #32325d;
    font-weight: 500;
    font-size: 14px;
    line-height: 1.5;
    display: block;
    word-wrap: break-word;
}

.kbli-definition {
    color: #8898aa;
    font-size: 12px;
    font-style: italic;
    line-height: 1.4;
    display: block;
    word-wrap: break-word;
}

.kbli-tree>>>.el-tree-node__expand-icon {
    font-size: 14px;
    color: #525f7f;
    margin-right: 8px;
}

.kbli-tree>>>.el-tree-node__expand-icon.is-leaf {
    color: transparent;
}

.kbli-tree>>>.el-tree-node {
    white-space: normal;
}

.kbli-tree>>>.el-tree-node__children {
    overflow: visible;
}
</style>